import Dexie, { type Table } from 'dexie';
import type { DailyLog, WeeklyAssessment, UserProfile, Medication } from '../types';

export class AllergoAwayDB extends Dexie {
  dailyLogs!: Table<DailyLog, number>;
  weeklyAssessments!: Table<WeeklyAssessment, number>;
  userProfile!: Table<UserProfile, number>;
  medications!: Table<Medication, number>;

  constructor() {
    super('AllergoAwayDB');
    this.version(1).stores({
      dailyLogs: '++id, date, createdAt',
      weeklyAssessments: '++id, weekStartDate, createdAt',
      userProfile: '++id',
      medications: '++id, name',
    });
    this.version(2).stores({
      dailyLogs: '++id, date, [date+entryTime], createdAt',
      weeklyAssessments: '++id, weekStartDate, createdAt',
      userProfile: '++id',
      medications: '++id, name',
    });
  }
}

export const db = new AllergoAwayDB();

// Get a specific log by date and entryTime
export async function getLogByDateAndTime(date: string, entryTime: string): Promise<DailyLog | undefined> {
  return db.dailyLogs.where({ date, entryTime }).first();
}

// Get the first/primary log for a date (backwards compatible)
export async function getLogByDate(date: string): Promise<DailyLog | undefined> {
  return db.dailyLogs.where('date').equals(date).first();
}

// Get all logs for a specific date (morning + evening + single)
export async function getLogsByDate(date: string): Promise<DailyLog[]> {
  return db.dailyLogs.where('date').equals(date).toArray();
}

export async function getAllLogs(): Promise<DailyLog[]> {
  return db.dailyLogs.orderBy('date').toArray();
}

export async function getLogsBetween(start: string, end: string): Promise<DailyLog[]> {
  return db.dailyLogs.where('date').between(start, end, true, true).toArray();
}

export async function saveLog(log: DailyLog): Promise<number> {
  const now = new Date().toISOString();
  if (log.id) {
    log.updatedAt = now;
    await db.dailyLogs.put(log);
    return log.id;
  } else {
    // Check if there's already an entry for this date + entryTime
    const existing = await getLogByDateAndTime(log.date, log.entryTime);
    if (existing) {
      log.id = existing.id;
      log.updatedAt = now;
      log.createdAt = existing.createdAt;
      await db.dailyLogs.put(log);
      return existing.id!;
    }
    log.createdAt = now;
    log.updatedAt = now;
    return db.dailyLogs.add(log);
  }
}

export async function getProfile(): Promise<UserProfile | undefined> {
  return db.userProfile.toCollection().first();
}

export async function saveProfile(profile: UserProfile): Promise<number> {
  const existing = await getProfile();
  if (existing) {
    profile.id = existing.id;
    await db.userProfile.put(profile);
    return existing.id!;
  }
  return db.userProfile.add(profile);
}

export async function getMedications(): Promise<Medication[]> {
  return db.medications.toArray();
}

export async function saveMedication(med: Medication): Promise<number> {
  if (med.id) {
    await db.medications.put(med);
    return med.id;
  }
  return db.medications.add(med);
}

export async function deleteMedication(id: number): Promise<void> {
  await db.medications.delete(id);
}

export async function getWeeklyAssessments(): Promise<WeeklyAssessment[]> {
  return db.weeklyAssessments.orderBy('weekStartDate').toArray();
}

export async function saveWeeklyAssessment(assessment: WeeklyAssessment): Promise<number> {
  const now = new Date().toISOString();
  assessment.createdAt = now;
  if (assessment.id) {
    await db.weeklyAssessments.put(assessment);
    return assessment.id;
  }
  return db.weeklyAssessments.add(assessment);
}

// Export all data for backup/sync
export async function exportAllData(): Promise<object> {
  const [logs, assessments, profile, meds] = await Promise.all([
    getAllLogs(),
    getWeeklyAssessments(),
    getProfile(),
    getMedications(),
  ]);
  return {
    version: 2,
    exportedAt: new Date().toISOString(),
    dailyLogs: logs,
    weeklyAssessments: assessments,
    userProfile: profile || null,
    medications: meds,
  };
}

// Import data from backup
export async function importAllData(data: any): Promise<void> {
  await db.transaction('rw', db.dailyLogs, db.weeklyAssessments, db.userProfile, db.medications, async () => {
    if (data.dailyLogs) {
      for (const log of data.dailyLogs) {
        const existing = await getLogByDateAndTime(log.date, log.entryTime || 'single');
        if (existing) {
          if (log.updatedAt > existing.updatedAt) {
            log.id = existing.id;
            await db.dailyLogs.put(log);
          }
        } else {
          delete log.id;
          await db.dailyLogs.add(log);
        }
      }
    }
    if (data.weeklyAssessments) {
      for (const a of data.weeklyAssessments) {
        const existing = await db.weeklyAssessments.where('weekStartDate').equals(a.weekStartDate).first();
        if (existing) {
          a.id = existing.id;
          await db.weeklyAssessments.put(a);
        } else {
          delete a.id;
          await db.weeklyAssessments.add(a);
        }
      }
    }
    if (data.userProfile) {
      delete data.userProfile.id;
      const existing = await getProfile();
      if (!existing) {
        await db.userProfile.add(data.userProfile);
      } else {
        data.userProfile.id = existing.id;
        await db.userProfile.put(data.userProfile);
      }
    }
    if (data.medications) {
      await db.medications.clear();
      for (const m of data.medications) {
        delete m.id;
        await db.medications.add(m);
      }
    }
  });
}
